import type * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Download } from "lucide-react";
import { Button, Loader } from "@/ui/atoms";
import { EmptyState } from "@/ui/molecules";
import { cn } from "@/utils/utils";
import type {
    TApiOperatingStatement,
    TApiOperatingStatementAccount,
    TApiOperatingStatementGroup,
    TApiOperatingStatementSection,
} from "@/apis/proda_finance/types";

// Its own formatter, not the shared accounting one in ./amountFormat (brackets,
// dash for zero) that the Comparison tab keeps: the statement shows plain
// minus-signed amounts with 0.00, like the agreed operating statement design.
const AMOUNT_FORMATTER = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const MONTH_FORMATTER = new Intl.DateTimeFormat("en-GB", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
});

/** Plain two-decimal amount with a minus sign; null (no baseline TB) as an em dash. */
export function formatStatementAmount(value: string | null): string {
    if (value === null) return "—";
    // `|| 0` folds a "-0.00" from the API into a plain zero.
    return AMOUNT_FORMATTER.format(Number(value) || 0);
}

function formatMonth(isoDate: string): string {
    return MONTH_FORMATTER.format(new Date(`${isoDate.slice(0, 10)}T00:00:00Z`));
}

/** How the month's figures were read from the file — shown above the table. */
function amountColumnCaption(statement: TApiOperatingStatement): string {
    return statement.amount_basis === "opening_balance"
        ? "Closing − opening balance"
        : "As reported in the file";
}

const ALL_PROPERTIES = "";
const BELOW_THE_LINE = "below-the-line";

function groupId(side: TApiOperatingStatementSection["side"], group: TApiOperatingStatementGroup) {
    return `${side}:${group.key}`;
}

function expandableIds(statement: TApiOperatingStatement): string[] {
    const ids = [statement.income, statement.expense].flatMap((section) =>
        section.groups.map((group) => groupId(section.side, group)),
    );
    return statement.unclassified_rows.length > 0 ? [...ids, BELOW_THE_LINE] : ids;
}

export type TOperatingStatementTabProps = {
    fileId: string;
    loadStatement: (
        fileId: string,
        propertyCode: string | undefined,
    ) => Promise<TApiOperatingStatement>;
    onDownload: (fileId: string, propertyCode: string | undefined) => Promise<void>;
    canDownload: boolean;
};

type TLoadState =
    | { status: "loading" }
    | { status: "error" }
    | { status: "ready"; statement: TApiOperatingStatement };

/**
 * The Trial Balance shown as an operating statement: an Income and an Operating
 * Expenses section of CoA groups, each folding open with an arrow to its accounts,
 * and Net Operating Income = Effective Gross Income − Total Operating Expenses, all
 * for the TB's month (read from the file alone: closing − opening when it carries
 * opening balances).
 */
export function OperatingStatementTab(props: TOperatingStatementTabProps): React.ReactElement {
    const { fileId, loadStatement, onDownload, canDownload } = props;
    const [propertyCode, setPropertyCode] = useState<string>(ALL_PROPERTIES);
    const [state, setState] = useState<TLoadState>({ status: "loading" });
    const [properties, setProperties] = useState<string[]>([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    // Groups start folded; the set survives a property switch, so the same groups
    // stay open while comparing properties.
    const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

    useEffect(() => {
        let cancelled = false;
        setState({ status: "loading" });
        loadStatement(fileId, propertyCode === ALL_PROPERTIES ? undefined : propertyCode)
            .then((statement) => {
                if (cancelled) return;
                setState({ status: "ready", statement });
                // The option list comes from the whole file, so it stays stable
                // while one property is selected.
                setProperties(statement.properties);
            })
            .catch(() => {
                if (!cancelled) setState({ status: "error" });
            });
        return () => {
            cancelled = true;
        };
    }, [fileId, propertyCode, loadStatement, reloadKey]);

    const handleDownload = useCallback(async () => {
        setIsDownloading(true);
        try {
            await onDownload(fileId, propertyCode === ALL_PROPERTIES ? undefined : propertyCode);
        } finally {
            setIsDownloading(false);
        }
    }, [fileId, propertyCode, onDownload]);

    const toggle = useCallback((id: string) => {
        setExpanded((current) => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const allIds = useMemo(
        () => (state.status === "ready" ? expandableIds(state.statement) : []),
        [state],
    );
    const allExpanded = allIds.length > 0 && allIds.every((id) => expanded.has(id));

    return (
        <div className="space-y-4" data-testid="operating-statement-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <label htmlFor="operating-statement-property" className="text-sm font-medium">
                        Property
                    </label>
                    <select
                        id="operating-statement-property"
                        data-testid="operating-statement-property"
                        value={propertyCode}
                        onChange={(e) => setPropertyCode(e.target.value)}
                        className="px-3 py-2 border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        <option value={ALL_PROPERTIES}>All properties</option>
                        {properties.map((code) => (
                            <option key={code} value={code}>
                                {code}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        data-testid="operating-statement-toggle-all"
                        onClick={() => setExpanded(allExpanded ? new Set() : new Set(allIds))}
                        disabled={allIds.length === 0}
                    >
                        {allExpanded ? "Collapse all" : "Expand all"}
                    </Button>
                    {canDownload && (
                        <Button
                            variant="outline"
                            data-testid="operating-statement-download"
                            onClick={() => void handleDownload()}
                            disabled={state.status !== "ready" || isDownloading}
                        >
                            <Download className="h-4 w-4 mr-2" />
                            Download Excel
                        </Button>
                    )}
                </div>
            </div>

            {state.status === "loading" ? (
                <div className="flex items-center justify-center py-12">
                    <Loader size="lg" />
                </div>
            ) : state.status === "error" ? (
                <EmptyState
                    variant="error"
                    title="Couldn't build the operating statement"
                    description="The statement for this file could not be loaded."
                    action={{ label: "Try again", onClick: () => setReloadKey((k) => k + 1) }}
                />
            ) : (
                <StatementBody statement={state.statement} expanded={expanded} onToggle={toggle} />
            )}
        </div>
    );
}

type TBodyProps = {
    statement: TApiOperatingStatement;
    expanded: ReadonlySet<string>;
    onToggle: (id: string) => void;
};

function StatementBody({ statement, expanded, onToggle }: TBodyProps): React.ReactElement {
    const hasLines =
        statement.income.groups.length > 0 ||
        statement.expense.groups.length > 0 ||
        statement.unclassified_rows.length > 0;
    if (!hasLines) {
        return (
            <EmptyState
                title="No income-statement accounts"
                description="None of this Trial Balance's lines map to a P&L account in the Chart of Accounts."
            />
        );
    }
    const unclassifiedTotal = statement.reconciliation.unclassified_total;

    return (
        <div className="space-y-3">
            {statement.unclassified_rows.length > 0 && (
                <div
                    className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
                    data-testid="operating-statement-unclassified-warning"
                >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                        {statement.unclassified_rows.length} account(s) on this TB are not in the
                        Chart of Accounts, so they cannot be classified and are excluded from Net
                        Operating Income ({formatStatementAmount(unclassifiedTotal)}). They are
                        listed below the line.
                    </span>
                </div>
            )}
            <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{formatMonth(statement.period)}</span>
                {" · "}
                <span data-testid="operating-statement-amount-basis">
                    {amountColumnCaption(statement)}
                </span>
            </p>
            <div className="max-h-[70vh] overflow-auto rounded-lg border">
                <table className="w-full text-sm" data-testid="operating-statement-table">
                    <colgroup>
                        <col />
                        <col className="w-48" />
                    </colgroup>
                    {/* No visible header row: the Income band carries the "Amount" title. */}
                    <thead className="sr-only">
                        <tr>
                            <th scope="col">Account</th>
                            <th scope="col">{formatMonth(statement.period)}</th>
                        </tr>
                    </thead>
                    {[statement.income, statement.expense].map((section, index) => (
                        <SectionRows
                            key={section.side}
                            section={section}
                            showAmountTitle={index === 0}
                            expanded={expanded}
                            onToggle={onToggle}
                        />
                    ))}
                    <tfoot className="sticky bottom-0 z-10">
                        <tr
                            className="border-t-2 border-primary/30 bg-primary/10 font-bold"
                            data-testid="operating-statement-net-income"
                        >
                            <td className="px-4 py-4 text-xl">Net Operating Income</td>
                            <td className={cn(AMOUNT_CELL, "py-4 text-xl")}>
                                {formatStatementAmount(statement.net_income)}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
            {statement.unclassified_rows.length > 0 && (
                <div className="overflow-x-auto rounded-lg border">
                    <table
                        className="w-full text-sm"
                        data-testid="operating-statement-below-the-line"
                    >
                        <colgroup>
                            <col />
                            <col className="w-48" />
                        </colgroup>
                        <tbody>
                            <ExpandableRow
                                label="Below the line – not included in Net Operating Income"
                                amount={unclassifiedTotal}
                                isExpanded={expanded.has(BELOW_THE_LINE)}
                                onToggle={() => onToggle(BELOW_THE_LINE)}
                                testId="operating-statement-group-below-the-line"
                                muted
                            />
                            {expanded.has(BELOW_THE_LINE) &&
                                statement.unclassified_rows.map((account) => (
                                    <AccountRow key={account.account_code} account={account} />
                                ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

type TSide = TApiOperatingStatementSection["side"];

/** The closing line of each section, level with its title. */
const SECTION_TOTAL_LABELS: Record<TSide, string> = {
    income: "Effective Gross Income",
    expense: "Total Operating Expenses",
};

const AMOUNT_CELL = "border-l border-border/50 px-4 text-right tabular-nums";

function SectionRows({
    section,
    showAmountTitle,
    expanded,
    onToggle,
}: {
    section: TApiOperatingStatementSection;
    showAmountTitle: boolean;
    expanded: ReadonlySet<string>;
    onToggle: (id: string) => void;
}): React.ReactElement {
    return (
        <tbody data-testid={`operating-statement-section-${section.side}`}>
            <tr className="bg-muted">
                <td className="px-4 py-2.5 text-base font-semibold">{section.label}</td>
                <td className={cn(AMOUNT_CELL, "py-2.5 text-xs font-medium text-muted-foreground")}>
                    {showAmountTitle ? "Amount" : null}
                </td>
            </tr>
            {section.groups.map((group) => {
                const id = groupId(section.side, group);
                const isExpanded = expanded.has(id);
                return (
                    <GroupRows
                        key={id}
                        group={group}
                        isExpanded={isExpanded}
                        onToggle={() => onToggle(id)}
                    />
                );
            })}
            <tr
                className="border-t bg-muted/60 font-semibold"
                data-testid={`operating-statement-total-${section.side}`}
            >
                <td className="px-4 py-2.5">{SECTION_TOTAL_LABELS[section.side]}</td>
                <td className={cn(AMOUNT_CELL, "py-2.5")}>
                    {formatStatementAmount(section.amount)}
                </td>
            </tr>
        </tbody>
    );
}

function GroupRows({
    group,
    isExpanded,
    onToggle,
}: {
    group: TApiOperatingStatementGroup;
    isExpanded: boolean;
    onToggle: () => void;
}): React.ReactElement {
    return (
        <>
            <ExpandableRow
                label={group.label}
                amount={group.amount}
                isExpanded={isExpanded}
                onToggle={onToggle}
                testId="operating-statement-group"
            />
            {isExpanded &&
                group.accounts.map((account) => (
                    <AccountRow key={account.account_code} account={account} />
                ))}
        </>
    );
}

function ExpandableRow({
    label,
    amount,
    isExpanded,
    onToggle,
    testId,
    muted = false,
}: {
    label: string;
    amount: string;
    isExpanded: boolean;
    onToggle: () => void;
    testId: string;
    muted?: boolean;
}): React.ReactElement {
    const Chevron = isExpanded ? ChevronDown : ChevronRight;
    return (
        <tr
            className={cn("border-t border-border/60", muted && "italic text-muted-foreground")}
            data-testid={testId}
        >
            <td className="py-1.5 pl-5 pr-2">
                <button
                    type="button"
                    aria-expanded={isExpanded}
                    onClick={onToggle}
                    className="flex w-full items-center gap-1.5 rounded px-2 py-0.5 text-left font-medium hover:bg-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                    <Chevron className="h-4 w-4 shrink-0" aria-hidden />
                    <span>{label}</span>
                </button>
            </td>
            <td className={cn(AMOUNT_CELL, "py-1.5")}>{formatStatementAmount(amount)}</td>
        </tr>
    );
}

function AccountRow({ account }: { account: TApiOperatingStatementAccount }): React.ReactElement {
    return (
        <tr
            className="border-t border-border/60 text-muted-foreground"
            data-testid="operating-statement-account"
        >
            {/* "{code} {name}", lined up with the group's name: past the arrow, not
                indented per CoA level. Only accounts carry a code; group lines don't. */}
            <td className="py-1.5 pl-[3.125rem] pr-4">
                <span className="mr-1.5 font-mono text-xs">{account.account_code}</span>{" "}
                <span>{account.label}</span>
            </td>
            <td className={cn(AMOUNT_CELL, "py-1.5 text-foreground")}>
                {formatStatementAmount(account.amount)}
            </td>
        </tr>
    );
}
